import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useRouter } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Moon, PanelLeftClose, PanelLeftOpen, Search, Sun } from 'lucide-react';
import { Icon } from '@/components/ui/icon.js';
import { Button } from '@/components/ui/button.js';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu.js';
import { BalancePill } from './BalancePill.js';
import { BrandLogo } from './BrandLogo.js';
import { AnnouncementsBell } from '@/features/announcements/AnnouncementsBell.js';
import { useAuthStore } from '@/stores/auth.js';
import { useBrandingStore } from '@/stores/branding.js';
import { useIsImpersonating } from '@/features/auth/useIsImpersonating.js';
import { useUiStore } from '@/stores/ui.js';
import { usePrefsStore } from '@/stores/prefs.js';
import { SUPPORTED_LOCALES } from '@/lib/i18n/index.js';
import { logoutMutation } from '@/features/auth/api.js';
import { toast } from 'sonner';
import { patchMemberMe, MEMBER_QUERY_KEYS } from '@/features/member/api.js';
import { ORIGINALS } from '@/features/originals/catalog.js';
import { OriginalIcon } from '@/features/originals/OriginalIcon.js';
import { originalName } from '@/features/originals/names.js';
import type { WalletSnapshot } from '@/lib/wallet-snapshot.js';

const LOCALE_LABELS: Record<string, string> = {
  en: 'English',
  th: 'ไทย',
  vi: 'Tiếng Việt',
  zh: '中文',
  km: 'ខ្មែរ',
  lo: 'ລາວ',
  my: 'မြန်မာ',
};

// Duel-style nav item: text only (no pill), white when active. The active
// underline bar is rendered separately via <ActiveBar/>.
function navItemCls(active: boolean): string {
  return [
    'relative inline-flex items-center gap-2 h-12 px-3.5 text-[16px] font-bold whitespace-nowrap transition-colors',
    active
      ? 'text-[var(--text-primary)]'
      : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
  ].join(' ');
}

// Duel-style active underline: a short rounded bar centred under the item text.
function ActiveBar() {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute bottom-1 left-1/2 -translate-x-1/2 h-[3px] w-7 rounded-full bg-[var(--accent-strong)] shadow-[0_0_10px_var(--accent-glow)]"
    />
  );
}

// Animated dropdown panel — fades + slides + scales in on hover/focus
// (origin top-left) so it eases open instead of snapping into existence.
const dropWrap = (w: string) =>
  [
    'absolute left-0 top-full pt-3 z-50',
    w,
    'opacity-0 -translate-y-2 scale-[0.97] pointer-events-none',
    'transition-[opacity,transform] duration-200 ease-out origin-top-left',
    'group-hover:opacity-100 group-hover:translate-y-0 group-hover:scale-100 group-hover:pointer-events-auto',
    'group-focus-within:opacity-100 group-focus-within:translate-y-0 group-focus-within:scale-100 group-focus-within:pointer-events-auto',
  ].join(' ');

const PANEL =
  'rounded-2xl border border-white/10 bg-[var(--bg-raised)]/95 backdrop-blur-xl shadow-[var(--shadow-modal)] p-2.5';
const DROP_ITEM = 'flex items-center gap-3 px-2.5 py-2 rounded-xl hover:bg-[var(--bg-elevated)] transition-colors';

function Chev() {
  return (
    <ChevronDown
      className="size-4 text-[var(--text-muted)] transition-transform duration-200 group-hover:rotate-180"
      aria-hidden
    />
  );
}

function DropHeader({ children }: { children: ReactNode }) {
  return (
    <div className="px-2.5 pt-1 pb-2 text-[11px] font-bold uppercase tracking-wider text-[var(--text-muted)]">
      {children}
    </div>
  );
}

function MenuTile({ code, label, sub }: { code: string; label: string; sub?: string }) {
  return (
    <>
      <OriginalIcon code={code} size={40} className="shadow-[0_4px_14px_rgba(0,0,0,.25)]" />
      <span className="min-w-0 flex-1">
        <span className="block text-[14px] font-bold leading-tight text-[var(--text-primary)] truncate">{label}</span>
        {sub && <span className="block text-[11px] text-[var(--text-muted)] truncate">{sub}</span>}
      </span>
    </>
  );
}


interface TopBarProps {
  wallet?: WalletSnapshot;
  username?: string;
  currentPath?: string;
}

export function TopBar({ wallet, username, currentPath }: TopBarProps) {
  const { clear, refreshToken } = useAuthStore();
  const subject = useAuthStore((s) => s.subject);
  const sitename = useBrandingStore((s) => s.branding?.sitename) ?? '';
  const isImpersonating = useIsImpersonating();
  const { theme, setTheme } = useUiStore();
  const setSearchOpen = useUiStore((s) => s.setSearchOpen);
  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebarCollapsed);
  const router = useRouter();
  const { t } = useTranslation('common');
  const locale = usePrefsStore((s) => s.locale);
  const setLocale = usePrefsStore((s) => s.setLocale);
  const qc = useQueryClient();
  const [scrolled, setScrolled] = useState(false);

  const languageMutation = useMutation({
    mutationFn: async (next: string) => {
      if (subject?.id) {
        await patchMemberMe(subject.id, { preferredLanguage: next });
      }
      return next;
    },
    onSuccess: (next) => {
      setLocale(next);
      if (subject?.id) void qc.invalidateQueries({ queryKey: MEMBER_QUERY_KEYS.me });
    },
  });

  // Transparent over the hero at the top; solid blurred (borderless) once scrolled.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const openSearch = useCallback(() => setSearchOpen(true), [setSearchOpen]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== '/') return;
      const tag = (e.target as HTMLElement).tagName;
      const editable = (e.target as HTMLElement).isContentEditable;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || editable) return;
      e.preventDefault();
      setSearchOpen(true);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [setSearchOpen]);

  const navActive = (to: string) =>
    to === '/' ? currentPath === '/' : currentPath === to || Boolean(currentPath?.startsWith(to + '/'));

  async function handleLogout() {
    await logoutMutation(refreshToken);
    clear();
    toast.success(t('logout.success'));
    router.navigate({ to: '/' });
  }

  return (
    <header
      className={[
        'sticky top-0 z-40 transition-colors duration-200 border-b bg-[var(--bg-sunken)]',
        scrolled ? 'border-white/[0.06]' : 'border-transparent',
      ].join(' ')}
    >
      <div className="flex h-20 items-center gap-4 lg:gap-6 w-full px-4 md:px-6">
        {/* Sidebar toggle — bc.game style, sits left of the logo. Hidden below
            lg where the rail itself is hidden (mobile uses the bottom nav). */}
        <button
          type="button"
          onClick={toggleSidebar}
          aria-label={t('common.toggleMenu')}
          className="hidden lg:grid size-11 shrink-0 place-items-center rounded-xl bg-[var(--bg-elevated)]/60 text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text-primary)] transition-colors"
        >
          {sidebarCollapsed
            ? <PanelLeftOpen className="size-5" aria-hidden />
            : <PanelLeftClose className="size-5" aria-hidden />}
        </button>

        <Link to="/" aria-label={sitename || undefined} className="shrink-0">
          <BrandLogo size={36} />
        </Link>

        {/* Main nav — big, borderless, animated Duel-style dropdowns */}
        <nav className="hidden md:flex items-center gap-1.5">
          {/* Originals — clicking goes to the lobby (the Originals showcase);
              hover still opens the dropdown of individual games. */}
          <div className="relative group">
            <Link to="/games/originals" className={navItemCls(navActive('/games/originals'))}>
              <Icon name="star" size={18} />
              {t('nav.originals')}
              <Chev />
              {navActive('/games/originals') && <ActiveBar />}
            </Link>
            <div className={dropWrap('w-72')}>
              <div className={PANEL}>
                <DropHeader>{t('nav.originals')}</DropHeader>
                {ORIGINALS.map((m) => (
                  <Link key={m.code} to="/games/originals/$code" params={{ code: m.code }} className={DROP_ITEM}>
                    <MenuTile code={m.code} label={originalName(m.code, t)} sub="Originals · Provably Fair" />
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Casino — plain link (no dropdown) */}
          <Link to="/casino" className={navItemCls(navActive('/casino'))}>
            <Icon name="cherry" size={18} />
            {t('nav.casino')}
            {navActive('/casino') && <ActiveBar />}
          </Link>

          {/* Sports — dedicated sportsbook page (seamless in-frame providers) */}
          <Link to="/sports" className={navItemCls(navActive('/sports'))}>
            <Icon name="trendUp" size={18} />
            {t('nav.sports')}
            {navActive('/sports') && <ActiveBar />}
          </Link>

          {/* Lotto — dedicated lottery page */}
          <Link to="/lottery" className={navItemCls(navActive('/lottery'))}>
            <Icon name="ticket" size={18} />
            {t('nav.lotto')}
            {navActive('/lottery') && <ActiveBar />}
          </Link>
        </nav>

        {/* Right side */}
        <div className="ml-auto flex items-center gap-2.5">
          {/* Search trigger — opens CommandPalette (also bound to / hotkey) */}
          <button
            type="button"
            aria-label={t('common.search')}
            onClick={openSearch}
            className="w-11 h-11 rounded-full grid place-items-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-elevated)]/60 transition-colors"
          >
            <Search className="size-5" aria-hidden />
          </button>

          <AnnouncementsBell />

          {wallet && <BalancePill wallet={wallet} />}

          {wallet && !isImpersonating && (
            <Link to="/wallet/deposit" className="hidden sm:inline-flex">
              <Button size="md" glow>
                <Icon name="arrowDown" size={15} />
                {t('common.deposit')}
              </Button>
            </Link>
          )}

          {/* Profile / avatar */}
          {username && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 h-11 pl-1.5 pr-2.5 md:pr-3 rounded-full border border-[var(--border)] hover:border-[var(--border-strong)] transition-colors group/avatar data-[state=open]:border-[var(--border-strong)]"
                >
                  <span className="w-8 h-8 rounded-full bg-[var(--primary)]/15 text-[var(--primary)] grid place-items-center font-extrabold text-xs">
                    {username.slice(0, 2).toUpperCase()}
                  </span>
                  <span className="hidden sm:inline text-[13px] font-semibold text-[var(--text-primary)]">{username}</span>
                  <ChevronDown
                    className="size-3.5 text-[var(--text-muted)] transition-transform group-data-[state=open]/avatar:rotate-180"
                    aria-hidden
                  />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 p-0">
                <DropdownMenuItem asChild className="rounded-none px-3 py-2.5">
                  <Link
                    to="/profile"
                    className="text-[13px] font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                  >
                    <Icon name="user" size={14} />
                    {t('nav.profile')}
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuItem asChild className="rounded-none px-3 py-2.5 border-t border-[var(--border-subtle)]">
                  <Link
                    to="/wallet"
                    className="text-[13px] font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                  >
                    <Icon name="wallet" size={14} />
                    {t('nav.wallet')}
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuItem asChild className="rounded-none px-3 py-2.5 border-t border-[var(--border-subtle)]">
                  <Link
                    to="/history"
                    className="text-[13px] font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                  >
                    <Icon name="history" size={14} />
                    {t('nav.history')}
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuItem asChild className="rounded-none px-3 py-2.5 border-t border-[var(--border-subtle)]">
                  <Link
                    to="/support"
                    className="text-[13px] font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                  >
                    <Icon name="headset" size={14} />
                    {t('nav.support')}
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuItem asChild className="rounded-none px-3 py-2.5 border-t border-[var(--border-subtle)]">
                  <Link
                    to="/promos"
                    search={{ tab: 'referral' }}
                    className="text-[13px] font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                  >
                    <Icon name="users" size={14} />
                    {t('nav.referral')}
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    setTheme(theme === 'dark' ? 'light' : 'dark');
                  }}
                  className="rounded-none px-3 py-2.5 text-[13px] font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)] border-t border-[var(--border-subtle)]"
                >
                  {theme === 'dark' ? <Sun className="size-3.5" aria-hidden /> : <Moon className="size-3.5" aria-hidden />}
                  {t('common.toggleTheme')}
                </DropdownMenuItem>

                <DropdownMenuItem asChild className="rounded-none px-3 py-2.5 border-t border-[var(--border-subtle)]">
                  <Link
                    to="/settings"
                    className="text-[13px] font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                  >
                    <Icon name="settings" size={14} />
                    {t('nav.settings')}
                  </Link>
                </DropdownMenuItem>

                <div className="border-t border-[var(--border-subtle)] px-3 py-2">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)] mb-1.5">
                    {t('nav.language')}
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    {SUPPORTED_LOCALES.map((l) => {
                      const active = l === locale;
                      return (
                        <button
                          key={l}
                          type="button"
                          disabled={languageMutation.isPending}
                          onClick={() => languageMutation.mutate(l)}
                          className={[
                            'flex items-center justify-between px-2 py-1 rounded text-[11px] font-semibold transition-colors',
                            active
                              ? 'bg-[var(--primary)] text-[var(--primary-on)]'
                              : 'text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)]',
                          ].join(' ')}
                        >
                          <span className="truncate">{LOCALE_LABELS[l] ?? l}</span>
                          <span className="font-mono text-[9px] opacity-60">{l}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <DropdownMenuSeparator className="mx-0 my-0" />
                <DropdownMenuItem
                  onSelect={() => void handleLogout()}
                  className="rounded-none px-3 py-2.5 text-[13px] font-semibold text-[var(--danger)] hover:bg-[var(--danger-bg)] data-[highlighted]:bg-[var(--danger-bg)] data-[highlighted]:text-[var(--danger)]"
                >
                  <Icon name="logout" size={14} />
                  {t('nav.signOut')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {!username && (
            <>
              <Link to="/login">
                <Button variant="ghost" size="md">{t('auth.signIn')}</Button>
              </Link>
              <Link to="/register">
                <Button size="md" className="bg-[var(--accent)] hover:bg-[var(--accent-strong)] text-[var(--accent-on)] shadow-[0_0_24px_var(--accent-glow)]">{t('auth.signUp')}</Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
